import _ from 'underscore';
import { decorate } from '@bingads-webui/dialog';
import {
  getSupportedSegmentationTree,
  isSegmentationTypeSupported,
  isTimeRangeValid,
  localizeSegmentationType,
  campaignSegmentationTypeEnum,
} from '@bingads-webui-campaign/segmentation-utilities';
import { get } from '@bingads-webui-universal/primitive-utilities';
import segment from './projection/segment';
import segmentCellStyle from './projection/segment-cell-style';
import segmentAlert from './segment-alert';

function changeEntitySet(queryParams, segType, edm) {
  const { options } = queryParams;
  const entitySet = edm.root.identifyEntitySet(options.entitySet.toJSON());

  const action = entitySet.navigation.source;
  const gridDataParameters = segType ?
    _.defaults({
      SegmentationTypes: [segType],
    }, entitySet.navigation.parameters.gridDataParameters) :
    _.omit(entitySet.navigation.parameters.gridDataParameters, 'SegmentationTypes');
  const parameters = _.defaults({ gridDataParameters }, entitySet.navigation.parameters);

  return _.defaults({
    options: _.defaults({
      entitySet: action.$call(parameters),
    }, options),
  }, queryParams);
}

function changeQueryParams(queryParams, segType) {
  const query = segType ? _.defaults({ SegmentationTypes: segType }, queryParams.query) : _.omit(queryParams.query, 'SegmentationTypes');

  return _.defaults({
    query,
  }, queryParams);
}

function updateSource(gridView, segType, isOdataActionSource, edm, isDataFetchManagerSource) {
  if (isDataFetchManagerSource) {
    gridView.refresh(true);
  } else {
    const type = segType && segType !== 'None' ? segType : null;
    const curQuery = gridView.get('query');
    const query = isOdataActionSource ?
      changeEntitySet(changeQueryParams(curQuery, type), type, edm) :
      changeQueryParams(curQuery, type);

    gridView.set({ query });
  }
}

export const segmentPlugin = definePlugin => definePlugin('segment', [
  'config',
  'gridView',
  'toolbar',
  'dateFilter',
], (config, gridView, toolbar, dateFilter) => {
  const menuId = 'dropdown-segment';
  const toolbarEditor = toolbar.gridToolbarGroup.toolbarWithItem(menuId);
  const pluginConfig = _.result(config.plugins, 'segment', {});
  const {
    isOdataActionSource,
    permissions,
    edm,
    i18n,
    setSegType,
    timeRangeValidationEnabled = true,
    instrumentation,
    getSegType,
  } = pluginConfig;
  const {
    currentScenario,
    isDataFetchManagerSource,
  } = config;
  let { segmentationTypes = campaignSegmentationTypeEnum } = pluginConfig;

  let activity;
  if (instrumentation) {
    const scenario = instrumentation.default.segmentMenu.create(currentScenario);

    activity = scenario.segmentMenu.create();
  }

  function logTrace(msg) {
    if (activity) {
      activity.trace(msg, 'segment');
    }
  }

  function validateSegmentType(segType) {
    if (timeRangeValidationEnabled &&
      !isTimeRangeValid(segType, dateFilter.getCurrentDateRange())) {
      const SegmentAlertDialog = decorate(segmentAlert);
      let segAlertView = new SegmentAlertDialog({
        dialogOptions: {
          title: i18n.getString('Segment_Time_Invalid_Alert_Title'),
          titleHeadingLevel: 2,
          maxWidth: 658,
          closeButtonAriaLabel: i18n.getString(_TL_('Close')),
        },
        segType,
        i18n,
      }).render();

      segAlertView.center();

      setTimeout(() => {
        // The segment menu will get the focus first, delay this behavior to acquire the focus
        const dialogConfirmButton = document.querySelector('.segment-alert-ok input');
        if (dialogConfirmButton) {
          dialogConfirmButton.focus();
        }
      }, 0);

      segAlertView.bind('ok', () => {
        segAlertView.destroy();
        segAlertView = null;

        const menuButton = document.querySelector(`#${menuId}`);
        if (menuButton) {
          menuButton.focus();
        }
      });

      return false;
    }
    return true;
  }

  function applySegment(segType) {
    return () => {
      if (validateSegmentType(segType)) {
        logTrace(`apply segment ${segType}`);
        const menu = getSegmentMenu(segType); // eslint-disable-line

        toolbarEditor.update(menu);

        setSegType(config.name, segType);

        updateSource(gridView, segType, isOdataActionSource, edm, isDataFetchManagerSource);
      } else {
        logTrace('failed to segment');
      }
    };
  }

  function getSegmentType() {
    if (isDataFetchManagerSource) {
      return get(getSegType(config.name), 'segmentationType', 'None');
    } else if (pluginConfig.isOdataActionSource) {
      return _.chain(gridView.get('queryParams'))
        .result('options')
        .result('entitySet')
        .result('navigation')
        .result('parameters')
        .result('gridDataParameters')
        .result('SegmentationTypes')
        .first()
        .value() || 'None';
    }

    return _.chain(gridView.get('query'))
      .result('query')
      .result('SegmentationTypes', 'None')
      .value();
  }

  function setSegmentType(segmentType, overrideSegmentationTypes) {
    if (overrideSegmentationTypes) {
      segmentationTypes = overrideSegmentationTypes;
    }
    const setter = applySegment(segmentType);

    setter();
  }

  function getAvailableSegmentationTypes() {
    return segmentationTypes;
  }

  function getSegmentMenu(segType) {
    function getMenuItem(seg) {
      const itemConfig = {
        text: localizeSegmentationType({ i18n, type: seg }),
        onClick: applySegment(seg),
        classes: [seg],
      };

      _.extend(itemConfig, {
        iconLeftGlyphName: segType === seg ? 'ba-CheckMark' : '',
      });
      return itemConfig;
    }

    const itemClasses = {
      Time: 'segment-time-sub-menu',
      Audience: 'segment-audience-type-sub-menu',
      Goal: 'segment-goal-type-sub-menu',
    };
    const segmentMenu = {
      iconLeftGlyphName: 'ba-SegmentInsights',
      type: 'dropdown',
      id: menuId,
      button: {
        text: i18n.getString('Segment_Dropdown_Title'),
      },
      ariaLabel: i18n.getString('Segment_Dropdown_Title'),
      menu: {
        items: _.map(getSupportedSegmentationTree({
          permissions,
          segmentationTypes,
        }), ({ children, name }) => {
          if (children) {
            const childMenuItems = _.map(children, childItem => getMenuItem(childItem.name));

            const isChild = _.any(children, childItem => childItem.name === segType);

            const menuItemWithChildren = {
              type: 'dropdown-submenu',
              button: {
                text: localizeSegmentationType({ i18n, type: name }),
                classes: [itemClasses[name]],
              },
              menu: {
                items: childMenuItems,
              },
            };

            _.extend(menuItemWithChildren.button, {
              iconLeftGlyphName: isChild ? 'ba-CheckMark' : '',
            });

            return menuItemWithChildren;
          }

          return getMenuItem(name);
        }),
      },
    };

    return segmentMenu;
  }

  function resetMenu() {
    const menu = getSegmentMenu('None');

    toolbarEditor.update(menu);
  }

  const cellTemplates = _.result(pluginConfig, 'cellTemplates', {});

  gridView.pipeStructureProjections(segment);
  gridView.pipeContentProjections(segmentCellStyle);

  gridView.set({
    segmentCellStyle: {
      cellTemplates,
      i18n,
    },
  });

  dateFilter.events.on('dateChange', () => {
    const curSegType = getSegmentType();

    if (curSegType && timeRangeValidationEnabled &&
      !isTimeRangeValid(curSegType, dateFilter.getCurrentDateRange())) {
      setSegType(config.name, 'None');
      updateSource(gridView, null, isOdataActionSource, edm, isDataFetchManagerSource);
      resetMenu();
    }
  });

  let segTypeInit = getSegType ?
    get(getSegType(config.name), 'segmentationType', 'None') :
    _.chain({})
      .defaults(config.query)
      .defaults(config.dataSource)
      .result('query')
      .result('SegmentationTypes', 'None')
      .value();

  if (!isSegmentationTypeSupported({ permissions, type: segTypeInit })) {
    segTypeInit = 'None';
  }

  logTrace(`init segment type is ${segTypeInit}`);
  updateSource(gridView, segTypeInit, isOdataActionSource, edm, isDataFetchManagerSource);
  const menu = getSegmentMenu(segTypeInit);

  toolbarEditor.update(menu);
  gridView.model.trigger('change');

  return {
    getSegmentType,
    setSegmentType,
    getAvailableSegmentationTypes,
    validateSegmentType,
  };
});

// when grid use the 'aggregatedRows', 'segment' plugin at the same time,
// need use the 'arrgeratedRows' firstly.
segmentPlugin.softDependencies = ['aggregatedRows'];
